/**
 * useNovaEnrichedContext — turns the current route into a rich context
 * bundle Nova can reason about without asking.
 *
 * Route → data:
 *   /clients                 → recent clients (up to 8)
 *   /studio/:projectId       → the active project + linked client + open tasks
 *   /calendar                → upcoming events (next 14 days, up to 8)
 *   /inquiries               → recent inquiries (up to 8)
 *   /billing/:projectId      → the active project + last invoices
 *   fallback                 → the route surface only
 *
 * The hook subscribes with React Query so refreshes are cheap and
 * cache-shared with the rest of the app.
 */
import { useLocation, useParams, matchPath } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { deriveNovaContext, type NovaSurface } from '@/lib/nova/context';

export interface NovaEnrichedContext extends NovaSurface {
  /** Compact record of the data Nova should treat as ground truth. */
  data?: Record<string, unknown>;
  /** True while the enrichment query is in flight. */
  loading?: boolean;
}

function matchProjectId(pathname: string): string | null {
  const m = matchPath({ path: '/studio/:projectId', end: false }, pathname)
    ?? matchPath({ path: '/billing/:projectId', end: false }, pathname);
  return (m?.params as { projectId?: string } | undefined)?.projectId ?? null;
}

export function useNovaEnrichedContext(): NovaEnrichedContext {
  const location = useLocation();
  useParams(); // ensure re-render on param change
  const surface = deriveNovaContext(location.pathname);
  const projectId = matchProjectId(location.pathname);
  const p = location.pathname.toLowerCase();

  const kind: string =
    projectId ? 'project'
    : p.startsWith('/calendar') ? 'calendar'
    : p.startsWith('/clients') ? 'clients'
    : p.startsWith('/inquiries') ? 'inquiries'
    : 'none';

  const query = useQuery({
    queryKey: ['nova-context', kind, projectId ?? location.pathname],
    enabled: kind !== 'none',
    staleTime: 30_000,
    queryFn: async (): Promise<Record<string, unknown> | null> => {
      if (kind === 'project' && projectId) {
        const [proj, tasks] = await Promise.all([
          supabase
            .from('projects')
            .select('id, name, status, stage, budget, due_date, description, client:clients(id, name, email)')
            .eq('id', projectId)
            .maybeSingle(),
          supabase
            .from('tasks')
            .select('id, title, status, priority, due_date')
            .eq('project_id', projectId)
            .neq('status', 'completed')
            .order('due_date', { ascending: true, nullsFirst: false })
            .limit(10),
        ]);
        return { project: proj.data ?? null, open_tasks: tasks.data ?? [] };
      }
      if (kind === 'calendar') {
        const now = new Date().toISOString();
        const in14 = new Date(Date.now() + 14 * 86400_000).toISOString();
        const { data } = await supabase
          .from('calendar_events')
          .select('id, title, start_at, end_at, location, project_id')
          .gte('start_at', now)
          .lte('start_at', in14)
          .order('start_at', { ascending: true })
          .limit(8);
        return { upcoming_events: data ?? [] };
      }
      if (kind === 'clients') {
        const { data } = await supabase
          .from('clients')
          .select('id, name, email, phone, brand')
          .order('updated_at', { ascending: false })
          .limit(8);
        return { recent_clients: data ?? [] };
      }
      if (kind === 'inquiries') {
        const { data } = await supabase
          .from('inquiries')
          // LAUNCH-5 — inquiries date by `submitted_at`; there is no
          // `created_at` column, so this read failed on every visit.
          .select('id, name, email, status, submitted_at')
          .order('submitted_at', { ascending: false })
          .limit(8);
        return { recent_inquiries: data ?? [] };
      }
      return null;
    },
  });

  return {
    ...surface,
    data: (query.data ?? undefined) as Record<string, unknown> | undefined,
    loading: query.isLoading,
  };
}